import { Image } from 'react-bootstrap';
import { calculateDiscountedPrice } from '../utils/discountUtils';

// Proper printable invoice for an order. Rendered inside OrderScreen and
// targeted by the print stylesheet (see .invoice-receipt in styles.css) so
// the existing "Print Receipt" button prints only this, not the whole page.
const OrderReceipt = ({ order, customerName, customerEmail }) => {
    let savings = 0;
    order.orderItems.forEach((item) => {
        if (item.isClearance) {
            const originalLineTotal = item.price * item.qty;
            const discountedLineTotal = calculateDiscountedPrice(item.price, item.discount ?? 0) * item.qty;
            savings += originalLineTotal - discountedLineTotal;
        }
    });

    const isPaid = !order.isPreOrder;

    return (
        <div className="invoice-receipt">
            <div className="invoice-header">
                <Image
                    src="/images/Logos/RDLogoBlack.png"
                    alt="RetroDayz"
                    className="invoice-logo"
                />
                <div className="invoice-header-meta">
                    <div className="invoice-title">INVOICE</div>
                    <div className="invoice-company">
                        RetroDayz<br />
                        Liverpool, United Kingdom<br />
                        support@retrodayz.co.uk<br />
                        retrodayz.co.uk
                    </div>
                </div>
            </div>

            <div className="invoice-divider" />

            <div className="invoice-info-grid">
                <div>
                    <div className="invoice-label">Billed To</div>
                    <div className="invoice-value">
                        <strong>{customerName}</strong><br />
                        {customerEmail && <>{customerEmail}<br /></>}
                        {order.shippingAddress.address}<br />
                        {order.shippingAddress.city}, {order.shippingAddress.postalCode}<br />
                        {order.shippingAddress.country}
                    </div>
                </div>
                <div>
                    <div className="invoice-label">Invoice Details</div>
                    <div className="invoice-value">
                        <span className="invoice-detail-row"><span>Order Number:</span> <strong>{order.orderNumber || order._id}</strong></span>
                        <span className="invoice-detail-row"><span>Order Date:</span> {new Date(order.createdAt).toLocaleDateString('en-GB')}</span>
                        <span className="invoice-detail-row"><span>Payment Method:</span> {order.paymentMethod}</span>
                        <span className="invoice-detail-row">
                            <span>Status:</span>{' '}
                            <strong className={isPaid ? 'invoice-status-paid' : 'invoice-status-pending'}>
                                {isPaid ? 'PAID' : 'AUTHORIZED — PENDING DISPATCH'}
                            </strong>
                        </span>
                        {order.paymentResult?.id && (
                            <span className="invoice-detail-row"><span>Transaction ID:</span> {order.paymentResult.id}</span>
                        )}
                    </div>
                </div>
            </div>

            <table className="invoice-items-table">
                <thead>
                    <tr>
                        <th className="text-start">Item</th>
                        <th className="text-center">Qty</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {order.orderItems.map((item, index) => {
                        const discountedPrice = calculateDiscountedPrice(item.price, item.discount ?? 0);
                        return (
                            <tr key={index}>
                                <td>
                                    {item.name}
                                    {item.size && <span className="invoice-item-sub"> — Size: {item.size}</span>}
                                    {item.isClearance && <span className="invoice-item-sub"> — Clearance</span>}
                                    {item.isPreOrder && <span className="invoice-item-sub"> — Pre-Order</span>}
                                </td>
                                <td className="text-center">{item.qty}</td>
                                <td className="text-end">
                                    {item.isClearance ? (
                                        <>
                                            <span className="invoice-strike">£{item.price.toFixed(2)}</span>{' '}
                                            £{discountedPrice.toFixed(2)}
                                        </>
                                    ) : (
                                        `£${item.price.toFixed(2)}`
                                    )}
                                </td>
                                <td className="text-end">
                                    £{(item.qty * (item.isClearance ? discountedPrice : item.price)).toFixed(2)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className="invoice-totals">
                <div className="invoice-totals-row">
                    <span>Subtotal</span>
                    <span>£{order.itemsPrice.toFixed(2)}</span>
                </div>

                {savings > 0 && (
                    <div className="invoice-totals-row invoice-totals-discount">
                        <span>Savings</span>
                        <span>-£{savings.toFixed(2)}</span>
                    </div>
                )}

                {order.promoCode && order.promoDiscount > 0 && (
                    <div className="invoice-totals-row invoice-totals-discount">
                        <span>Promo Code ({order.promoCode})</span>
                        <span>-£{order.promoDiscount.toFixed(2)}</span>
                    </div>
                )}

                <div className="invoice-totals-row">
                    <span>Shipping</span>
                    <span>{order.shippingPrice === 0 ? 'FREE' : `£${order.shippingPrice.toFixed(2)}`}</span>
                </div>

                {order.taxPrice > 0 && (
                    <div className="invoice-totals-row">
                        <span>Includes VAT</span>
                        <span>£{order.taxPrice.toFixed(2)}</span>
                    </div>
                )}

                <div className="invoice-totals-row invoice-totals-grand">
                    <span>{order.isPreOrder ? 'Total Authorized' : 'Total Paid'}</span>
                    <span>£{order.totalPrice.toFixed(2)}</span>
                </div>
            </div>

            <div className="invoice-footer">
                Thank you for shopping with RetroDayz.<br />
                Questions about this order? Contact support@retrodayz.co.uk
            </div>
        </div>
    );
};

export default OrderReceipt;
